import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createInstitution,
  getRegisterOptions,
  registerUser,
} from "../../services/authService";
import {
  isBornAfterYear,
  isUnderThirteen,
  normalizeRegisterValues,
  validateRegisterValues,
} from "../../utils/registerValidation";
import { passwordRequirementsMessage } from "../../utils/passwordValidation";
import { BrandField } from "../form/BrandField";
import { BrandButton } from "../ui/BrandButton";
import { FormCard } from "../form/FormCard";
import { BrandSelect } from "../form/BrandSelect";
import { InstitutionSearchSelect } from "../form/InstitutionSearchSelect";
import { UruguayPhoneField } from "../form/UruguayPhoneField";
import { brandFormCardClass } from "../form/fieldStyles";
import { useAppLoading } from "../../context/AppLoadingContext";

const initialValues = {
  name: "",
  lastName: "",
  cellphone: "",
  birthDate: "",
  cityOfOrigin: "",
  username: "",
  email: "",
  password: "",
  passwordConfirm: "",
  institutionSelection: "",
  institutionName: "",
  tnuaId: "",
  parentName: "",
  parentLastName: "",
  parentCellphone: "",
  parentEmail: "",
};

const schoolBirthYearLimit = 2007;
const tnuaBirthYearLimit = 2004;

const fieldLabels = {
  name: "Nombre",
  lastName: "Apellido",
  cellphone: "Número de celular",
  birthDate: "Fecha de nacimiento",
  cityOfOrigin: "Ciudad de origen",
  username: "Nombre de usuario",
  email: "Correo electrónico",
  institutionSelection: "Si vas a la escuela/liceo, ¿a cuál?",
  institutionName: "Nueva escuela/liceo",
  tnuaId: "Si vas a tnuá, ¿a cuál?",
  password: "Contraseña",
  parentName: "Nombre del padre, madre o tutor",
  parentLastName: "Apellido del padre, madre o tutor",
  parentCellphone: "Celular del padre, madre o tutor",
  parentEmail: "Correo del padre, madre o tutor",
};

export function RegisterForm({ logoSrc }) {
  const { trackAsync } = useAppLoading();
  const navigate = useNavigate();
  const [values, setValues] = useState(initialValues);
  const [touched, setTouched] = useState({});
  const [serverFieldErrors, setServerFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [successData, setSuccessData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [isCreatingInstitution, setIsCreatingInstitution] = useState(false);
  const [optionsError, setOptionsError] = useState("");
  const [formResetKey, setFormResetKey] = useState(0);
  const [registrationOptions, setRegistrationOptions] = useState({
    institutions: [],
    tnuaOptions: [],
  });

  const fieldErrors = useMemo(() => validateRegisterValues(values), [values]);
  const requiresParentInfo = isUnderThirteen(values.birthDate);
  const requiresSchoolInfo = isBornAfterYear(values.birthDate, schoolBirthYearLimit);
  const requiresTnuaInfo = isBornAfterYear(values.birthDate, tnuaBirthYearLimit);

  useEffect(() => {
    let isMounted = true;

    const loadOptions = async () => {
      try {
        const response = await getRegisterOptions();

        if (!isMounted) {
          return;
        }

        setRegistrationOptions({
          institutions: response.institutions ?? [],
          tnuaOptions: response.tnuaOptions ?? [],
        });
        setOptionsError("");
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setOptionsError(error.message || "No pudimos cargar las opciones del registro.");
      } finally {
        if (isMounted) {
          setIsLoadingOptions(false);
        }
      }
    };

    void trackAsync(loadOptions);

    return () => {
      isMounted = false;
    };
  }, [trackAsync]);

  const showError = (field) => touched[field] && fieldErrors[field];

  const clearServerErrors = (fields) => {
    setServerFieldErrors((current) => {
      const next = { ...current };
      let changed = false;

      for (const field of fields) {
        if (next[field]) {
          delete next[field];
          changed = true;
        }
      }

      return changed ? next : current;
    });
  };

  const mapBackendFieldToFormField = (field) => {
    if (field === "institutionId") {
      return "institutionSelection";
    }

    return field;
  };

  const handleCreateInstitution = async (institutionName) => {
    setIsCreatingInstitution(true);
    setSubmitError("");
    setServerFieldErrors({});

    try {
      const response = await createInstitution({ name: institutionName });
      const createdInstitution = response.institution;

      if (createdInstitution?.id) {
        setRegistrationOptions((current) => {
          const exists = current.institutions.some(
            (institution) => institution.id === createdInstitution.id,
          );

          return {
            ...current,
            institutions: exists
              ? current.institutions.map((institution) =>
                  institution.id === createdInstitution.id ? createdInstitution : institution,
                )
              : [...current.institutions, createdInstitution].sort((a, b) =>
                  a.name.localeCompare(b.name, "es"),
                ),
          };
        });
      }

      return createdInstitution;
    } catch (error) {
      if (error.details && typeof error.details === "object" && !Array.isArray(error.details)) {
        const field = error.details.field === "name" ? "institutionSelection" : error.details.field;
        setServerFieldErrors((current) => ({
          ...current,
          [field]: error.message,
        }));
        return null;
      }

      setSubmitError(error.message || "No pudimos crear la institución.");
      return null;
    } finally {
      setIsCreatingInstitution(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setValues((current) => {
      const next = {
        ...current,
        [name]:
          name === "username" || name === "email" || name === "parentEmail"
            ? value.toLowerCase()
            : value,
      };

      if (name === "institutionSelection" && value !== "__new__") {
        next.institutionName = "";
      }

      if (name === "birthDate" && !isUnderThirteen(value)) {
        next.parentName = "";
        next.parentLastName = "";
        next.parentCellphone = "";
        next.parentEmail = "";
      }

      if (name === "birthDate" && !isBornAfterYear(value, schoolBirthYearLimit)) {
        next.institutionSelection = "";
        next.institutionName = "";
      }

      if (name === "birthDate" && !isBornAfterYear(value, tnuaBirthYearLimit)) {
        next.tnuaId = "";
      }

      return next;
    });

    setSubmitError("");
    setSuccessData(null);

    if (name === "institutionSelection") {
      clearServerErrors(["institutionSelection", "institutionName", "institutionId"]);
      return;
    }

    if (name === "birthDate") {
      clearServerErrors([
        "birthDate",
        "cellphone",
        "email",
        "institutionSelection",
        "institutionName",
        "tnuaId",
        "parentName",
        "parentLastName",
        "parentCellphone",
        "parentEmail",
      ]);
      return;
    }

    clearServerErrors([name]);
  };

  const handleBlur = (event) => {
    const { name } = event.target;
    setTouched((current) => ({ ...current, [name]: true }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const normalized = normalizeRegisterValues(values);
    const errors = validateRegisterValues(normalized);

    setTouched({
      name: true,
      lastName: true,
      cellphone: true,
      birthDate: true,
      cityOfOrigin: true,
      username: true,
      email: true,
      institutionSelection: true,
      institutionName: true,
      tnuaId: true,
      password: true,
      passwordConfirm: true,
      parentName: true,
      parentLastName: true,
      parentCellphone: true,
      parentEmail: true,
    });

    if (Object.keys(errors).length > 0) {
      return;
    }

    const payload = {
      name: normalized.name,
      lastName: normalized.lastName,
      cellphone: requiresParentInfo ? normalized.parentCellphone : normalized.cellphone,
      birthDate: normalized.birthDate,
      cityOfOrigin: normalized.cityOfOrigin || undefined,
      username: normalized.username,
      email: requiresParentInfo ? normalized.parentEmail : normalized.email,
      password: normalized.password,
      passwordConfirm: normalized.passwordConfirm,
      tnuaId: requiresTnuaInfo ? normalized.tnuaId || undefined : undefined,
      institutionId:
        requiresSchoolInfo && normalized.institutionSelection &&
        normalized.institutionSelection !== "__new__"
          ? normalized.institutionSelection
          : undefined,
      institutionName:
        requiresSchoolInfo && normalized.institutionSelection === "__new__"
          ? normalized.institutionName
          : undefined,
      parentName: requiresParentInfo ? normalized.parentName : undefined,
      parentLastName: requiresParentInfo ? normalized.parentLastName : undefined,
      parentCellphone: requiresParentInfo ? normalized.parentCellphone : undefined,
      parentEmail: requiresParentInfo ? normalized.parentEmail : undefined,
    };

    setIsSubmitting(true);
    setSubmitError("");
    setServerFieldErrors({});

    try {
      await registerUser(payload);
      setValues(initialValues);
      setTouched({});
      setFormResetKey((current) => current + 1);
      navigate("/", { replace: true });
    } catch (error) {
      if (error.code === "AGE_RESTRICTION") {
        setSubmitError(error.message);
        return;
      }

      if (error.code === "USERNAME_TAKEN") {
        setServerFieldErrors((current) => ({
          ...current,
          username: error.message,
        }));
        return;
      }

      if (error.details && typeof error.details === "object" && !Array.isArray(error.details)) {
        const field = mapBackendFieldToFormField(error.details.field);
        if (field) {
          setServerFieldErrors((current) => ({
            ...current,
            [field]: error.message,
          }));
          return;
        }
      }

      if (Array.isArray(error.details)) {
        const mappedErrors = error.details.reduce((acc, item) => {
          if (item?.path) {
            acc[mapBackendFieldToFormField(item.path)] = item.message;
          }
          return acc;
        }, {});
        if (Object.keys(mappedErrors).length > 0) {
          setServerFieldErrors(mappedErrors);
          return;
        }
      }

      setSubmitError(error.message || "No pudimos completar el registro.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormCard className={brandFormCardClass}>
      <div className="mb-6 flex flex-col items-center text-center">
        <h2 className="mt-5 font-display text-3xl text-brand-primary sm:text-4xl">
          Creá tu cuenta
        </h2>
      </div>

      {optionsError ? (
        <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {optionsError}
        </div>
      ) : null}

      {submitError ? (
        <div className="mb-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {submitError}
        </div>
      ) : null}

      <form className="grid gap-4" onSubmit={handleSubmit} noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <BrandField
            label={fieldLabels.name}
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            error={showError("name") || serverFieldErrors.name}
            autoComplete="given-name"
            required
            showLabel={false}
            placeholder={fieldLabels.name}
          />
          <BrandField
            label={fieldLabels.lastName}
            name="lastName"
            value={values.lastName}
            onChange={handleChange}
            onBlur={handleBlur}
            error={showError("lastName") || serverFieldErrors.lastName}
            autoComplete="family-name"
            required
            showLabel={false}
            placeholder={fieldLabels.lastName}
          />
        </div>

        <BrandField
          label={fieldLabels.birthDate}
          name="birthDate"
          type="date"
          value={values.birthDate}
          onChange={handleChange}
          onBlur={handleBlur}
          error={showError("birthDate") || serverFieldErrors.birthDate}
          autoComplete="bday"
          showLabel={false}
        />

        {requiresParentInfo ? (
          <div className="grid gap-4 rounded-3xl border border-brand-primary/15 bg-brand-primary/5 p-4">
            <div className="grid gap-1">
              <h3 className="font-display text-lg text-brand-primary">
                Datos del padre, madre o tutor
              </h3>
              <p className="text-sm leading-6 text-slate-600">
                Como sos menor de 13 años, necesitamos los datos de una persona responsable.
                Usaremos su celular y correo como contacto de la cuenta. Podés registrar dos
                usuarios con el mismo contacto.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <BrandField
                label={fieldLabels.parentName}
                name="parentName"
                value={values.parentName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={showError("parentName") || serverFieldErrors.parentName}
                autoComplete="given-name"
                required
                showLabel={false}
                placeholder={fieldLabels.parentName}
              />
              <BrandField
                label={fieldLabels.parentLastName}
                name="parentLastName"
                value={values.parentLastName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={showError("parentLastName") || serverFieldErrors.parentLastName}
                autoComplete="family-name"
                required
                showLabel={false}
                placeholder={fieldLabels.parentLastName}
              />
            </div>

            <UruguayPhoneField
              label={fieldLabels.parentCellphone}
              name="parentCellphone"
              value={values.parentCellphone}
              onChange={handleChange}
              onBlur={handleBlur}
              error={showError("parentCellphone") || serverFieldErrors.parentCellphone}
              required
              showLabel={false}
            />

            <BrandField
              label={fieldLabels.parentEmail}
              name="parentEmail"
              type="email"
              value={values.parentEmail}
              onChange={handleChange}
              onBlur={handleBlur}
              error={showError("parentEmail") || serverFieldErrors.parentEmail}
              autoComplete="email"
              required
              showLabel={false}
              placeholder={fieldLabels.parentEmail}
            />
          </div>
        ) : (
          <div className="grid gap-4">
            <UruguayPhoneField
              label={fieldLabels.cellphone}
              name="cellphone"
              value={values.cellphone}
              onChange={handleChange}
              onBlur={handleBlur}
              error={showError("cellphone") || serverFieldErrors.cellphone}
              required
              showLabel={false}
            />
            <BrandField
              label={fieldLabels.email}
              name="email"
              type="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={showError("email") || serverFieldErrors.email}
              autoComplete="email"
              required
              showLabel={false}
              placeholder={fieldLabels.email}
            />
          </div>
        )}

        <BrandField
          label={fieldLabels.username}
          name="username"
          value={values.username}
          onChange={handleChange}
          onBlur={handleBlur}
          error={showError("username") || serverFieldErrors.username}
          autoComplete="username"
          helper="Acordátelo, porque lo vas a necesitar para iniciar sesión."
          showLabel={false}
          placeholder={fieldLabels.username}
        />

        <BrandField
          label={fieldLabels.cityOfOrigin}
          name="cityOfOrigin"
          value={values.cityOfOrigin}
          onChange={handleChange}
          onBlur={handleBlur}
          error={showError("cityOfOrigin") || serverFieldErrors.cityOfOrigin}
          autoComplete="address-level2"
          showLabel={false}
          placeholder={fieldLabels.cityOfOrigin}
        />

        {requiresSchoolInfo ? (
          <InstitutionSearchSelect
            key={formResetKey}
            label={fieldLabels.institutionSelection}
            name="institutionSelection"
            value={values.institutionSelection}
            institutionName={values.institutionName}
            onChange={handleChange}
            onBlur={handleBlur}
            onCreateOption={handleCreateInstitution}
            disabled={isLoadingOptions || isCreatingInstitution}
            required
            placeholder={
              isLoadingOptions
                ? "Cargando escuelas/liceos..."
                : "Si vas a la escuela/liceo, ¿a cuál?"
            }
            showLabel={false}
            options={[
              ...(isLoadingOptions
                ? []
                : registrationOptions.institutions.map((institution) => ({
                    value: institution.id,
                    label: institution.name,
                  }))),
            ]}
            error={
              showError("institutionSelection") ||
              showError("institutionName") ||
              serverFieldErrors.institutionSelection ||
              serverFieldErrors.institutionName
            }
          />
        ) : null}

        {requiresTnuaInfo ? (
          <BrandSelect
            label={fieldLabels.tnuaId}
            name="tnuaId"
            value={values.tnuaId}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isLoadingOptions}
            required
            placeholder={isLoadingOptions ? "Cargando tnua..." : "Si vas a tnuá, ¿a cuál?"}
            showLabel={false}
            options={registrationOptions.tnuaOptions.map((option) => ({
              value: option.id,
              label: option.name,
            }))}
            error={showError("tnuaId") || serverFieldErrors.tnuaId}
          />
        ) : null}

        <BrandField
          label={fieldLabels.password}
          name="password"
          value={values.password}
          onChange={handleChange}
          onBlur={handleBlur}
          error={showError("password") || serverFieldErrors.password}
          autoComplete="new-password"
          helper={passwordRequirementsMessage}
          required
          type={showPassword ? "text" : "password"}
          actionLabel={showPassword ? "Ocultar" : "Mostrar"}
          onActionClick={() => setShowPassword((current) => !current)}
          showLabel={false}
          placeholder={fieldLabels.password}
        />

        <BrandField
          label="Repetir contraseña"
          name="passwordConfirm"
          value={values.passwordConfirm}
          onChange={handleChange}
          onBlur={handleBlur}
          error={showError("passwordConfirm") || serverFieldErrors.passwordConfirm}
          autoComplete="new-password"
          required
          type={showPassword ? "text" : "password"}
          actionLabel={showPassword ? "Ocultar" : "Mostrar"}
          onActionClick={() => setShowPassword((current) => !current)}
          showLabel={false}
          placeholder="Repetir contraseña"
        />

        <BrandButton
          type="submit"
          disabled={isSubmitting || isLoadingOptions || isCreatingInstitution || Boolean(optionsError)}
          variant="primary"
          radius="xl"
          size="lg"
          className="w-full"
        >
          {isSubmitting ? "Registrando..." : "Crear cuenta"}
        </BrandButton>

        {successData ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            Cuenta creada para <strong>{successData.username}</strong>.
          </div>
        ) : null}
      </form>
    </FormCard>
  );
}
